import React from 'react';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import Error404 from 'screens/errors/404/Error404';
import { Logout } from 'components/logout/Logout';
import { getUserData } from 'helpers/user/getUserData';
import {
  isUserManagementAllowed,
  isUserViewer,
  isUserDepartmentManager,
} from 'helpers/user/isUserManagementAllowed';
import { getUserAccountType } from 'helpers/user/getUserAccountType';
import { ClientUserAccessType } from '../enums/ClientUserAccessType';
import { isUserBlackoutEditEnabled } from 'helpers/user/isUserBlackoutEditEnabled';
import { isUserAccountUser } from 'helpers/user/isUserAccountUser';
import { isOnAdminSide } from 'helpers/user/isOnAdminSide';
import { useCustomerBrandHotelSelection } from 'components/customerBrandHotelSelect/useCustomerBrandHotelSelection';
import { AccessType } from '../enums/AccessType';
import { includes, split } from 'lodash';
import { isMylqaSubscriptionActive } from 'helpers/hotel/isMylqaSubscriptionActive';
import { LqaUserAccessType } from '../enums/LqaUserAccessType';

const EditBlackout = React.lazy(
  () => import('screens/blackoutDate/customer/CustomerEditBlackoutDate')
);
const Page = React.lazy(() => import('screens/page/Page'));
const Account = React.lazy(() => import('screens/account/Account'));
const BrandDashboard = React.lazy(
  () => import('screens/brandDashboard/BrandDashboard')
);
const BrandBenchmarking = React.lazy(
  () => import('screens/brandReports/brandBenchmarking/BrandBenchmarking')
);
const BrandMissedStandards = React.lazy(
  () => import('screens/brandReports/brandMissedStandards/BrandMissedStandards')
);
const BrandTrends = React.lazy(
  () => import('screens/brandReports/brandTrends/BrandTrends')
);
const Blackouts = React.lazy(
  () => import('screens/blackoutDate/customer/CustomerBlackoutDates')
);
const AddBlackout = React.lazy(
  () => import('screens/blackoutDate/customer/CustomerAddBlackoutDate')
);
const ClientUsers = React.lazy(
  () => import('screens/clientUser/customer/CustomerClientUsers')
);
const AddClientUser = React.lazy(
  () => import('screens/clientUser/customer/CustomerAddClientUser')
);
const EditClientUser = React.lazy(
  () => import('screens/clientUser/customer/CustomerEditClientUser')
);
const HotelDashboard = React.lazy(
  () => import('screens/hotelDashboard/HotelDashboard')
);
const HotelBenchmarking = React.lazy(
  () => import('screens/hotelReports/hotelBenchmarking/HotelBenchmarking')
);
const HotelTrends = React.lazy(
  () => import('screens/hotelReports/hotelTrends/HotelTrends')
);
const HotelMissedStandards = React.lazy(
  () => import('screens/hotelReports/hotelMissedStandards/HotelMissedStandards')
);
const BrandAssessmentSummary = React.lazy(
  () =>
    import('screens/brandReports/brandAssessmentSummary/BrandAssessmentSummary')
);
const MyTraining = React.lazy(
  () => import('screens/onlineTraining/myTraining/MyTraining')
);
const MyLqaSubscription = React.lazy(
  () => import('screens/myLqaSubscription/MyLqaSubscription')
);
const OnlineTrainingLibrary = React.lazy(
  () =>
    import('screens/onlineTraining/onlineTrainingLibrary/OnlineTrainingLibrary')
);
const AssignTraining = React.lazy(
  () => import('screens/onlineTraining/assignTraining/AssignTraining')
);
const TrainingSummary = React.lazy(
  () => import('screens/onlineTraining/trainingSummary/TrainingSummary')
);
const CustomerActionPlan = React.lazy(
  () => import('screens/actionPlan/CustomerActionPlan')
);
const CustomerAddActionPlan = React.lazy(
  () => import('screens/actionPlan/add/CustomerAddActionPlan')
);
const CustomerEditActionPlan = React.lazy(
  () => import('screens/actionPlan/edit/CustomerEditActionPlan')
);
const AddSelfAssessment = React.lazy(
  () => import('screens/selfAssessments/addSelfAssessment/AddSelfAssessment')
);
const SelfAssessments = React.lazy(
  () => import('screens/selfAssessments/SelfAssessments')
);
const ActionPlanSummary = React.lazy(
  () => import('screens/actionPlanSummary/CustomerActionPlanSummary')
);
const HotelAssessmentSummary = React.lazy(
  () =>
    import('screens/hotelReports/hotelAssessmentSummary/HotelAssessmentSummary')
);
const EditSelfAssessment = React.lazy(
  () => import('screens/selfAssessments/editSelfAssessment/EditSelfAssessment')
);
const SelfAssessmentSummary = React.lazy(
  () =>
    import(
      'screens/selfAssessments/SelfAssessmentSummary/SelfAssessmentSummary'
    )
);
const SelfAssessmentsTrends = React.lazy(
  () => import('screens/selfAssessments/SelfAssessmentsAnalytics')
);
const SiteUsage = React.lazy(() => import('screens/siteUsage/SiteUsage'));
const NewStandards = React.lazy(
  () => import('screens/onlineTraining/standards/Standards')
);
const UpfrontTrainingAboutUs = React.lazy(
  () => import('screens/upfrontTraining/UpfrontTrainingAboutUs')
);
const UpfrontTrainingCurriculum = React.lazy(
  () => import('screens/upfrontTraining/UpfrontTrainingCurriculum')
);
const UpfrontTrainingEstimatedFeedback = React.lazy(
  () => import('screens/upfrontTraining/UpfrontTrainingEstimatedFeedback')
);
const UpfrontTrainingContactUpfront = React.lazy(
  () => import('screens/upfrontTraining/UpfrontTrainingContactUpfront')
);
const LoginSSO = React.lazy(() => import('screens/login/LoginSSO'));
const LoginSAML = React.lazy(() => import('screens/login/LoginSAML'));
const GlobalReports = React.lazy(
  () => import('screens/hotelReports/hotelBenchmarking/Globalreports')
);
const AdditionalReports = React.lazy(
  () => import('screens/additionalReports/AdditionalReports')
);
const MyLqaClientUsers = React.lazy(
  () => import('screens/myLqaClientUser/customer/CustomerMyLqaClientUsers')
);
const AddMyLqaClientUser = React.lazy(
  () => import('screens/myLqaClientUser/customer/CustomerAddMyLqaClientUser')
);
const EditMyLqaClientUser = React.lazy(
  () => import('screens/myLqaClientUser/customer/CustomerEditMyLqaClientUser')
);
const UploadMyLqaClientUser = React.lazy(
  () =>
    import('screens/myLqaClientUser/customer/CustomerMyLqaClientUserBulkUpload')
);
const CustomerNewsFeed = React.lazy(
  () => import('screens/newsFeed/customer/CustomerNewsFeed')
);
const CustomerAddNewsFeed = React.lazy(
  () => import('screens/newsFeed/customer/CustomerAddNewsFeed')
);
const CustomerEditNewsFeed = React.lazy(
  () => import('screens/newsFeed/customer/CustomerEditNewsFeed')
);
const AddTemplate = React.lazy(
  () => import('screens/myAudit/addTemplate/AddMyAuditTemplate')
);
const MyAuditOverview = React.lazy(
  () => import('screens/myAudit/list/MyAuditOverview')
);
const MyAuditView = React.lazy(
  () => import('screens/myAudit/view/MyAuditView')
);
const EditTemplate = React.lazy(
  () => import('screens/myAudit/editTemplate/EditMyAuditTemplate')
);
const Templates = React.lazy(() => import('screens/myAudit/MyAuditTemplates'));
const EditTemplateProperties = React.lazy(
  () => import('screens/myAudit/editTemplate/MyAuditEditTemplate')
);

// Post Assessment Roadmap
const PostAssessmentRoadmapOverview = React.lazy(
  () =>
    import(
      'screens/PostAssessmentRoadmap/PostAssessmentRoadmapOverview/PostAssessmentRoadmapOverview'
    )
);

const PostAssessmentByDepartment = React.lazy(
  () =>
    import(
      'screens/PostAssessmentRoadmap/PostAssessmentByDepartment/PostAssessmentByDepartment'
    )
);

const isUserAllowedToTrainingSummary = (userData) => {
  return isUserManagementAllowed(userData) || isUserAccountUser(userData);
};

const isUserAllowedToSelfAssessmentSummary = (userData) => {
  return isUserManagementAllowed(userData) || isUserAccountUser(userData);
};

const isUserAllowedToActionPlanSummary = (userData) => {
  return isUserManagementAllowed(userData) || isUserAccountUser(userData);
};

const isUserAllowedToBrandTrends = (userData) => {
  return getUserAccountType(userData) !== ClientUserAccessType.hotelUser;
};

const isUserAllowedToManageUsers = (userData) => {
  return isUserManagementAllowed(userData);
};

const isUserAllowedToBlackouts = (userData) => {
  return isUserBlackoutEditEnabled(userData);
};

const isUserAllowedToSiteUsage = (userData) => {
  const accountType = getUserAccountType(userData);
  if (userData.isViewer) {
    return false;
  }
  return (
    accountType === ClientUserAccessType.brandClientUser ||
    accountType === ClientUserAccessType.clientUser ||
    accountType === ClientUserAccessType.masterClientUser ||
    accountType === ClientUserAccessType.regionalClientUser ||
    accountType === ClientUserAccessType.hotelUser
  );
};

const isBrandUser = (userData) => {
  const accountType = getUserAccountType(userData);

  return accountType === ClientUserAccessType.brandClientUser;
};
// tslint:disable-next-line:cyclomatic-complexity
const PublicRouter = () => {
  const onAdminSide = isOnAdminSide(useLocation());
  const userData = getUserData(onAdminSide);
  const accountType = getUserAccountType(userData);
  const { selectedHotel, selectedBrand, selectedClient } =
    useCustomerBrandHotelSelection();

  return (
    <React.Suspense fallback={<LoadingContent layout="absolute" />}>
      <Switch>
        <Route exact={true} path="/sso/login/" component={LoginSSO} />
        <Route exact={true} path="/sso/saml/login/" component={LoginSAML} />
        <Route
          path="/login/"
          render={() => {
            if (isUserViewer(userData)) {
              return <Redirect to="/action-plans/action-plans/" />;
            }
            if (isBrandUser(userData)) {
              return <Redirect to="/brand/dashboard/" />;
            }
            if (
              accountType === LqaUserAccessType.LQAAdmin ||
              accountType === LqaUserAccessType.LQABasic
            ) {
              return <Redirect to="/admin/assessments/assessments/" />;
            }
            return <Redirect to="/hotel/dashboard/" />;
          }}
        />
        <Route path="/my-account/" exact={true} component={Account} />
        <Route path="/globalreports/" exact={true} component={GlobalReports} />
        <Route path="/my-audit/add-template/" component={AddTemplate} />
        <Route
          path="/my-audit/edit-template/:id/:departmentId?"
          component={EditTemplate}
        />
        <Route
          path="/my-audit/edit-template-properties/:id"
          component={EditTemplateProperties}
        />
        <Route
          path="/myLQA/my-audit-checklist"
          exact={true}
          component={Templates}
        />
        <Route
          path="/myLQA/my-audit-overview"
          exact={true}
          component={MyAuditOverview}
        />
        <Route
          path="/myLQA/my-audit-overview/:id/:assignedTo/:dueDate"
          exact={true}
          component={MyAuditView}
        />
        {selectedBrand &&
          !includes(
            split(Env.RESTRICT_BRAND_FOR_AMAN, ','),
            selectedBrand.id.toString()
          ) && (
            <Route path="/brand/trends/" exact={true} component={BrandTrends} />
          )}
        {selectedBrand &&
          !includes(
            split(Env.RESTRICT_BRAND_FOR_AMAN, ','),
            selectedBrand.id.toString()
          ) &&
          isUserViewer(userData) === false && [
            <Route
              path="/brand/dashboard/"
              exact={true}
              component={BrandDashboard}
              key={1}
            />,
            <Route
              path="/brand/assessment-summary/"
              exact={true}
              component={BrandAssessmentSummary}
              key={2}
            />,
            <Route
              path="/brand/benchmarking/"
              exact={true}
              component={BrandBenchmarking}
              key={3}
            />,
            <Route
              path="/brand/missed-standards/"
              exact={true}
              component={BrandMissedStandards}
              key={4}
            />,
          ]}
        // check for the aman brand
        {(() =>
          selectedBrand &&
          includes(
            split(Env.RESTRICT_BRAND_FOR_AMAN, ','),
            selectedBrand.id.toString()
          ) &&
          accountType !== AccessType.hotelUser &&
          accountType !== AccessType.regionalClientUser && (
            <Route path="/brand/trends/" exact={true} component={BrandTrends} />
          ))()}
        {(() =>
          selectedBrand &&
          includes(
            split(Env.RESTRICT_BRAND_FOR_AMAN, ','),
            selectedBrand.id.toString()
          ) &&
          accountType !== AccessType.hotelUser &&
          accountType !== AccessType.regionalClientUser &&
          isUserViewer(userData) === false && [
            <Route
              path="/brand/dashboard/"
              exact={true}
              component={BrandDashboard}
              key={1}
            />,
            <Route
              path="/brand/assessment-summary/"
              exact={true}
              component={BrandAssessmentSummary}
              key={2}
            />,
            <Route
              path="/brand/benchmarking/"
              exact={true}
              component={BrandBenchmarking}
              key={3}
            />,
            <Route
              path="/brand/missed-standards/"
              exact={true}
              component={BrandMissedStandards}
              key={4}
            />,
          ])()}
        {isUserViewer(userData) === false && [
          <Route
            path="/hotel/dashboard/"
            exact={true}
            component={HotelDashboard}
            key={5}
          />,
          <Route
            path="/hotel/benchmarking/"
            exact={true}
            component={HotelBenchmarking}
            key={6}
          />,
          <Route
            path="/hotel/missed-standards/"
            exact={true}
            component={HotelMissedStandards}
            key={7}
          />,
          <Route
            path="/hotel/assessment-summary/"
            exact={true}
            component={HotelAssessmentSummary}
            key={8}
          />,
          <Route
            path="/hotel/reservation-call-summary/"
            exact={true}
            component={HotelAssessmentSummary}
            key={9}
          />,
          <Route
            path="/hotel/events-summary/"
            exact={true}
            component={HotelAssessmentSummary}
            key={10}
          />,
          <Route
            path="/hotel/trends/"
            exact={true}
            component={HotelTrends}
            key={11}
          />,
          <Route
            path="/hotel/guest-service-summary/"
            exact={true}
            component={HotelAssessmentSummary}
            key={12}
          />,
          <Route
            path="/hotel/additional-assessment/"
            exact={true}
            component={HotelAssessmentSummary}
            key={13}
          />,
        ]}
        {isUserViewer(userData) === false && (
          <Route
            path="/action-plans/add-action-plan"
            exact={true}
            component={CustomerAddActionPlan}
          />
        )}
        <Route
          path="/action-plans/edit-action-plan/:id/:assessmentType"
          exact={true}
          component={CustomerEditActionPlan}
        />
        <Route
          path="/action-plans/view-action-plan/:id/:assessmentType"
          exact={true}
          component={CustomerEditActionPlan}
        />
        <Route
          path="/action-plans/action-plans/"
          exact={true}
          component={CustomerActionPlan}
        />
        {(isUserAllowedToActionPlanSummary(userData) ||
          isUserDepartmentManager(userData) === true) && (
          <Route
            path="/action-plans/summary/"
            exact={true}
            component={ActionPlanSummary}
          />
        )}
        {isUserAllowedToBlackouts(userData) && [
          <Route
            path="/management/blackouts/add-blackout"
            exact={true}
            component={AddBlackout}
            key={1}
          />,
          <Route
            path="/management/blackouts/edit-blackout/:id"
            component={EditBlackout}
            key={3}
          />,
          <Route
            path="/management/blackouts/"
            exact={true}
            component={Blackouts}
            key={2}
          />,
        ]}
        {isUserAllowedToManageUsers(userData) && [
          <Route
            path="/management/users/"
            exact={true}
            component={ClientUsers}
            key={3}
          />,
          <Route
            path="/management/users/add-user/"
            exact={true}
            component={AddClientUser}
            key={4}
          />,
          <Route
            path="/management/users/edit-user/:id"
            exact={true}
            component={EditClientUser}
            key={5}
          />,
          <Route
            path="/management/mylqa-users/"
            exact={true}
            component={MyLqaClientUsers}
            key={3}
          />,
          <Route
            path="/management/mylqa-users/add-mylqa-user/"
            exact={true}
            component={AddMyLqaClientUser}
            key={4}
          />,
          <Route
            path="/management/mylqa-users/edit-mylqa-user/:id"
            exact={true}
            component={EditMyLqaClientUser}
            key={5}
          />,
          <Route
            path="/management/users/upload-mylqa-user/"
            exact={true}
            component={UploadMyLqaClientUser}
            key={5}
          />,
        ]}
        {isMylqaSubscriptionActive(userData) && [
          <Route
            path="/myLQA/users/"
            exact={true}
            component={ClientUsers}
            key={1}
          />,
          <Route
            path="/myLQA/users/upload-mylqa-user/"
            exact={true}
            component={UploadMyLqaClientUser}
            key={2}
          />,
          <Route
            path="/myLQA/users/add-user/"
            exact={true}
            component={AddClientUser}
            key={2}
          />,
          <Route
            path="/myLQA/news-feed/"
            exact={true}
            component={CustomerNewsFeed}
            key={6}
          />,
          <Route
            path="/myLQA/news-feed/add"
            exact={true}
            component={CustomerAddNewsFeed}
            key={7}
          />,
          <Route
            path="/myLQA/news-feed/edit/:id"
            exact={true}
            component={CustomerEditNewsFeed}
            key={8}
          />,
        ]}
        {isUserAllowedToSiteUsage(userData) && (
          <Route
            path="/management/site-usage/"
            exact={true}
            component={SiteUsage}
          />
        )}
        {isUserManagementAllowed(userData) && (
          <Route
            path="/myLQA/mylqa-subscription/"
            exact={true}
            component={MyLqaSubscription}
          />
        )}
        <Route
          path="/management/additional-reports/"
          exact={true}
          component={AdditionalReports}
        />
        <Route
          path="/self-assessments/self-assessments/edit-self-assessment/:id/:departmentId?"
          exact={true}
          component={EditSelfAssessment}
        />
        <Route
          path="/self-assessments/self-assessments/view-self-assessment/:id/:departmentId?"
          exact={true}
          component={EditSelfAssessment}
        />
        {isUserViewer(userData) === false && (
          <Route
            path="/self-assessments/add-self-assessment/"
            exact={true}
            component={AddSelfAssessment}
          />
        )}
        {((isUserAllowedToSiteUsage(userData) &&
          isUserAllowedToSelfAssessmentSummary(userData)) ||
          isUserDepartmentManager(userData) === true) && (
          <Route
            path="/self-assessments/self-assessment-summary/"
            exact={true}
            component={SelfAssessmentSummary}
          />
        )}
        <Route
          path="/self-assessments/self-assessments/"
          component={SelfAssessments}
        />
        <Route
          path="/self-assessments/trends/"
          component={SelfAssessmentsTrends}
        />
        <Route
          path="/onlinetraining/mytraining"
          exact={true}
          component={MyTraining}
        />
        <Route
          path="/onlinetraining/e-training"
          exact={true}
          component={OnlineTrainingLibrary}
        />
        <Route
          path="/onlinetraining/take"
          exact={true}
          component={OnlineTrainingLibrary}
        />
        <Route
          path="/upfront/about-us"
          exact={true}
          component={UpfrontTrainingAboutUs}
        />
        <Route
          path="/upfront/our-curriculum"
          exact={true}
          component={UpfrontTrainingCurriculum}
        />
        <Route
          path="/upfront/extended-feedback"
          exact={true}
          component={UpfrontTrainingEstimatedFeedback}
        />
        <Route
          path="/upfront/contact-upfront"
          exact={true}
          component={UpfrontTrainingContactUpfront}
        />
        {!isUserViewer(userData) && (
          <Route
            path="/upfront/assign"
            exact={true}
            component={AssignTraining}
          />
        )}
        <Route
          path="/onlinetraining/assign"
          exact={true}
          component={AssignTraining}
        />
        {isUserAllowedToTrainingSummary(userData) && (
          <Route
            path="/upfront/summary"
            exact={true}
            component={TrainingSummary}
          />
        )}
        <Route
          strict={true}
          path={'/page/:path+/'}
          exact={true}
          component={Page}
        />
        <Route
          strict={true}
          path={'/page/:path*/:page'}
          exact={true}
          component={Page}
        />
        <Route path="/logout/" exact={true} component={Logout} />
        <Route
          path="/"
          exact={true}
          render={() => {
            if (isUserViewer(userData)) {
              return <Redirect to="/action-plans/action-plans/" />;
            }
            if (isBrandUser(userData)) {
              return <Redirect to="/brand/dashboard/" />;
            }
            if (
              accountType === LqaUserAccessType.LQAAdmin ||
              accountType === LqaUserAccessType.LQABasic
            ) {
              return <Redirect to="/admin/assessments/assessments/" />;
            }
            return <Redirect to="/hotel/dashboard/" />;
          }}
        />
        {/*TODO need to add relevant components that will be rendered on each page*/}
        <Route
          path="/postAssessmentRoadmap/:assessmentId/"
          exact={true}
          component={PostAssessmentRoadmapOverview}
        />
        <Route
          path="/postAssessmentRoadmap/:assessmentId/:departmentId/:subDepartmentId?"
          exact={true}
          component={PostAssessmentByDepartment}
        />
        <Route path="/" component={Error404} />
      </Switch>
    </React.Suspense>
  );
};

export default PublicRouter;
